import React, {PureComponent} from "react";
import Slider from "react-slick";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {FormattedMessage} from "react-intl";
import {ScaleLoader} from "react-spinners";
import {NewsItemEntity, NewsItemLinkEntity, NewsItemSubscriptionType} from "../data/entities/NewsItemEntity";
import {AdobeAnalyticsContext} from "../context/AdobeAnalyticsContext";
import {UserProgressionInfoEntity} from "../data/entities/UserProgressionInfoEntity";
import {subscriptionTypes} from "../data/entities/UserSubscriptionInfoEntity";

interface Props {
    news: NewsItemEntity[];
    handleGameAction: Function;
    userProgression: UserProgressionInfoEntity | null;
}

interface State {
    displayedNews: NewsItemEntity[];
}

export default class FeaturedNews extends PureComponent<Props, State> {
    static contextType = AdobeAnalyticsContext;
    context!: React.ContextType<typeof AdobeAnalyticsContext>;

    state: State = {
        displayedNews: []
    };

    componentDidMount(): void {
        this.processDisplayedNews();
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        if(prevProps.news !== this.props.news) {
            this.processDisplayedNews();
        }
    }

    getNewsItemLink = (newsItem: NewsItemEntity): NewsItemLinkEntity | null => {
        if (newsItem.links.length > 0) {
            return newsItem.links[0];
        }
        return null;
    }

    handleExternalAction = (
        newsItem: NewsItemEntity,
        newsItemLink: NewsItemLinkEntity | null,
        index: number
    ) => {
        if (newsItemLink) {
            this.context.track("clickevent", {
                location: `hero banner : ${index + 1}`,
                category: "interaction",
                action: `read news ${newsItem.newsId}`
            });
            window.open(newsItemLink.param, '_blank');
        }
    };

    handleGameAction = (newsItem: NewsItemEntity, index: number) => {
        if (newsItem.game) {
            this.context.track("clickevent", {
                location: `hero banner : ${index + 1}`,
                category: "interaction",
                action: `cta game : ${newsItem.game.name} ${newsItem.newsId}`
            });
            this.props.handleGameAction(newsItem.game);
        }
    };

    processDisplayedNews = () => {
        let displayedNews = this.props.news.filter(this.filterNewsByGame).filter(this.filterNewsByUserSubscription);

        displayedNews.sort((a: NewsItemEntity, b: NewsItemEntity) => {
            return a.priority - b.priority;
        });

        this.setState({
            displayedNews: displayedNews
        });
    }

    filterNewsByGame = (newsItem: NewsItemEntity): boolean => {
        if (!newsItem.game) {
            return true;
        } else {
            if(newsItem.obj.hideIfActivated && newsItem.game.ownership === 1) {
                return false;
            } else if(newsItem.obj.hideIfOwned && newsItem.game.ownership === 2) {
                return false;
            }
            return true;
        }
    };

    filterNewsByUserSubscription = (newsItem: NewsItemEntity): boolean => {
        if (!newsItem.obj.subscriptionType || !this.props.userProgression) {
            return true;
        } else {
            switch (newsItem.obj.subscriptionType) {
                case NewsItemSubscriptionType.freetrial:
                    if (
                        this.props.userProgression.subscriptionInfo.type === subscriptionTypes.freeTrialBasic ||
                        this.props.userProgression.subscriptionInfo.type === subscriptionTypes.freeTrialPremium
                    ) {
                        return true;
                    }
                    break;

                case NewsItemSubscriptionType.basic:
                    if (this.props.userProgression.subscriptionInfo.type === subscriptionTypes.basic) {
                        return true;
                    }
                    break;

                case NewsItemSubscriptionType.premium:
                    if (this.props.userProgression.subscriptionInfo.type === subscriptionTypes.premium) {
                        return true;
                    }
                    break;

                default:
                    return false;
            }

            return false;
        }
    };

    getCTAButtonLabel = (itemLink: NewsItemLinkEntity) => {
        let ctaLabel = "external.links.actions.go";

        if (itemLink.type === "External") {
            switch (itemLink.actionName) {
                case "moreinfo":
                    ctaLabel = "external.links.actions.more_info";
                    break;
                case "learnmore":
                    ctaLabel = "external.links.actions.learn_more";
                    break;
            }
        }

        return ctaLabel;
    }

    render(): JSX.Element {
        let NextSlideButton = (props: any) => {
            const { currentSlide, onClick } = props;

            return (
                <div
                    className="slide-action-next"
                    onClick={() => {
                        if (onClick) {
                            this.context.track("clickevent", {location: `hero banner : ${currentSlide + 1}`, category: "interaction", action: "next slide"});
                            onClick();
                        }
                    }}
                >
                    <FontAwesomeIcon icon="chevron-right" className="material-icons slide-nav-buttons" />
                </div>
            );
        };

        let PrevSlideButton = (props: any) => {
            const { currentSlide, onClick } = props;

            return (
                <div
                    className="slide-action-prev"
                    onClick={() => {
                        if (onClick) {
                            this.context.track("clickevent", {location: `hero banner : ${currentSlide + 1}`, category: "interaction", action: "previous slide"});
                            onClick();
                        }
                    }}
                >
                    <FontAwesomeIcon icon="chevron-left" className="material-icons slide-nav-buttons" />
                </div>
            );
        };

        let settings = {
            dots: true,
            infinite: true,
            speed: 500,
            slidesToShow: 1,
            slidesToScroll: 1,
            nextArrow: <NextSlideButton />,
            prevArrow: <PrevSlideButton />,
            swipeToSlide: true,
            autoplay: true,
            autoplaySpeed: 4000
        };

        return (
            <div className="featured-slider" id="nav-anchor-featured">
                <Slider {...settings}>
                    {
                        this.state.displayedNews.map((newsItem, index) => {
                            if (newsItem) {
                                let newsItemLink = this.getNewsItemLink(newsItem);
                                return (
                                    <div id={"game-"+newsItem.newsId} key={"game-"+newsItem.newsId} className="featured-item-slide">
                                        <div
                                            className={"slide-item" + (newsItem.game && newsItem.game.loading ? " loading" : "")}
                                            style={{backgroundImage: `url(${newsItem.mediaURL})`}}>
                                            <div className="slide-item-contents">
                                                <div className="slide-item-body">
                                                    <h3>{newsItem.title}</h3>
                                                    <p>{newsItem.body}</p>
                                                </div>

                                                <div className="slide-item-cta-holder">
                                                    {
                                                        newsItem.game &&
                                                        <React.Fragment>
                                                            <button onClick={() => this.handleGameAction(newsItem, index)} className={"actionButton"}>
                                                                <FormattedMessage id={"action.launch_game"}/>
                                                            </button>
                                                        </React.Fragment>
                                                    }
                                                    {
                                                        !newsItem.game && newsItemLink && newsItemLink.type === "External" &&
                                                        <React.Fragment>
                                                            <button
                                                                onClick={() => this.handleExternalAction(newsItem, newsItemLink, index)}
                                                                className={"actionButton"}
                                                            >
                                                                {
                                                                    <FormattedMessage id={this.getCTAButtonLabel(newsItem.links[0])} />
                                                                }
                                                            </button>
                                                        </React.Fragment>
                                                    }
                                                </div>
                                            </div>
                                        </div>

                                        {
                                            newsItem.game && newsItem.game.loading &&
                                            <div className="game-loader">
                                                <ScaleLoader color={'#fff'}/>
                                            </div>
                                        }
                                    </div>
                                );
                            } else {
                                return null;
                            }
                        })
                    }
                </Slider>
            </div>
        );
    }
}